import React, {
  useState,
  useEffect,
  useContext,
  FC,
} from 'react';
import styled from 'styled-components';
import { EventDatetimeContext } from 'components/EventDatetime';
import DinCondensedFontOTF from 'assets/fonts/D-DIN.otf';
import DinCondensedFontEOT from 'assets/fonts/D-DIN.eot';
import remoteTimeNow from 'libs/remote-time-now';

import CountdownField from './CountdownField';

const CountdownContainer = styled.div`
  @font-face {
    font-family: din;
    src:
      url(${DinCondensedFontOTF}),
      url(${DinCondensedFontEOT}?#iefix) format('embedded-opentype');
  }

  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  max-width: 568px;
  width: 100%;
  margin-top: 1%;
  padding-left: 20px;
  padding-right: 20px;

  @media screen and (min-height: 760px) {
    margin-top: 2%;
  }

  @media screen and (min-height: 1024px) {
    margin-top: 4%;
  }
`;

const justifyUnit = (unit, value) => (value > 1 ? `${unit}S` : unit);

const timeDiff = (destination, source) => ({
  days: destination.diff(source, 'days'),
  hours: destination.diff(source, 'hours') % 24,
  minutes: destination.diff(source, 'minutes') % 60,
  seconds: destination.diff(source, 'seconds') % 60,
});

const useCountdown = () => {
  const { startDatetime } = useContext(EventDatetimeContext);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      const newNow = remoteTimeNow();
      const td = timeDiff(startDatetime, newNow);
      setDays(td.days);
      setHours(td.hours);
      setMinutes(td.minutes);
      setSeconds(td.seconds);
    }, 1000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDatetime]);

  return {
    days, hours, minutes, seconds,
  };
};

export interface CountdownProps {
  id?: string;
}

const Countdown: FC<CountdownProps> = ({ id }: CountdownProps) => {
  const {
    days, hours, minutes, seconds,
  } = useCountdown();

  return (
    <CountdownContainer id={id}>
      <CountdownField
        unit={justifyUnit('DAY', days)}
        number={days}
      />
      <CountdownField
        unit={justifyUnit('HOUR', hours)}
        number={hours}
      />
      <CountdownField
        unit={justifyUnit('MINUTE', minutes)}
        number={minutes}
      />
      <CountdownField
        unit={justifyUnit('SECOND', seconds)}
        number={seconds}
      />
    </CountdownContainer>
  );
};

export default React.memo(Countdown);
